const MAX_DIMENSION = 1600;
const QUALITY = 0.82;

type Drawable = { source: CanvasImageSource; width: number; height: number; close: () => void };

/**
 * Avkodar en bild med createImageBitmap och faller tillbaka på <img> (Safari/iOS
 * klarar ofta format som createImageBitmap vägrar, t.ex. vissa HEIC/progressiva JPEG).
 */
export async function decodeImage(file: Blob): Promise<Drawable | null> {
  try {
    const bitmap = await createImageBitmap(file);
    return {
      source: bitmap,
      width: bitmap.width,
      height: bitmap.height,
      close: () => bitmap.close?.(),
    };
  } catch {
    // faller igenom till <img>
  }

  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = "async";
    img.src = url;
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("decode"));
    });
    if (!img.naturalWidth || !img.naturalHeight) throw new Error("decode");
    return {
      source: img,
      width: img.naturalWidth,
      height: img.naturalHeight,
      close: () => URL.revokeObjectURL(url),
    };
  } catch {
    URL.revokeObjectURL(url);
    return null;
  }
}

/**
 * Skalar ned och komprimerar en bild i webbläsaren innan uppladdning.
 * Resultatet är alltid en optimerad JPEG (vit bakgrund bakom genomskinlighet),
 * oavsett om källan var PNG, WEBP, AVIF, GIF, BMP eller ett kamerabildformat.
 * Returnerar originalet bara om webbläsaren inte kunde avkoda bilden alls.
 */
export async function compressImage(
  file: File | Blob,
  maxDimension = MAX_DIMENSION,
  quality = QUALITY,
): Promise<Blob> {
  try {
    const decoded = await decodeImage(file);
    if (!decoded) return file;
    const scale = Math.min(1, maxDimension / Math.max(decoded.width, decoded.height));
    const width = Math.max(1, Math.round(decoded.width * scale));
    const height = Math.max(1, Math.round(decoded.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      decoded.close();
      return file;
    }
    // Vit botten så att PNG/WEBP med genomskinlighet inte blir svart som JPEG.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(decoded.source, 0, 0, width, height);
    decoded.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality),
    );
    if (!blob) return file;
    // Behåll originalet bara om det redan är en mindre JPEG.
    const alreadyJpeg = /jpe?g/i.test(file.type);
    return alreadyJpeg && file.size <= blob.size ? file : blob;
  } catch {
    return file;
  }
}



/**
 * Roterar en base64-kodad bild 90° medurs (eller moturs) i webbläsaren.
 * Returnerar ny base64 (JPEG) utan data-URL-prefix.
 */
export async function rotateBase64(
  base64: string,
  mimeType = "image/jpeg",
  degrees: 90 | -90 = 90,
  quality = QUALITY,
): Promise<{ imageBase64: string; mimeType: string }> {
  const res = await fetch(`data:${mimeType};base64,${base64}`);
  const blob = await res.blob();
  const bitmap = await createImageBitmap(blob);

  const canvas = document.createElement("canvas");
  canvas.width = bitmap.height;
  canvas.height = bitmap.width;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    bitmap.close?.();
    return { imageBase64: base64, mimeType };
  }
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((degrees * Math.PI) / 180);
  ctx.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2);
  bitmap.close?.();

  const out = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", quality),
  );
  if (!out) return { imageBase64: base64, mimeType };

  const dataUrl = await new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.readAsDataURL(out);
  });
  return { imageBase64: dataUrl.split(",")[1], mimeType: "image/jpeg" };
}


/** Sant för iPhone-bilder (HEIC/HEIF) som de flesta webbläsare inte kan avkoda. */
export function isHeic(file: File | Blob): boolean {
  const type = file.type || "";
  const name = "name" in file ? (file as File).name : "";
  return /image\/(heic|heif)/i.test(type) || /\.(heic|heif)$/i.test(name);
}

/**
 * Gör vilken bildfil som helst (inkl. HEIC från iPhone) till en nedskalad JPEG-data-URL
 * som webbläsaren garanterat kan visa.
 */
export async function fileToJpegDataUrl(
  file: File,
  maxDimension = MAX_DIMENSION,
  quality = QUALITY,
): Promise<{ dataUrl: string; base64: string; mimeType: string }> {
  let blob: Blob = file;
  if (isHeic(file)) {
    try {
      const { heicTo } = await import("heic-to");
      blob = await heicTo({ blob: file, type: "image/jpeg", quality: 0.9 });
    } catch {
      // Safari kan avkoda HEIC nativt – låt canvas försöka i stället
    }
  }
  const out = await compressImage(blob, maxDimension, quality);
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Kunde inte läsa bilden"));
    reader.readAsDataURL(out);
  });
  const base64 = dataUrl.split(",")[1] ?? "";
  if (!base64) throw new Error("Kunde inte läsa bilden");
  return { dataUrl, base64, mimeType: out.type || "image/jpeg" };
}

/**
 * Beskär en base64-bild till en ruta i andelar (0–1) med lite marginal runt om.
 * Används av bildstudion för att klippa bort skräp innan AI:n redigerar bilden.
 */
export async function cropBase64(
  base64: string,
  mimeType = "image/jpeg",
  box: { x: number; y: number; w: number; h: number },
  margin = 0.08,
  quality = QUALITY,
): Promise<{ imageBase64: string; mimeType: string }> {
  try {
    const res = await fetch(`data:${mimeType};base64,${base64}`);
    const blob = await res.blob();
    const drawable = await decodeImage(blob);
    if (!drawable) return { imageBase64: base64, mimeType };

    const { width, height } = drawable;
    const padX = box.w * margin;
    const padY = box.h * margin;
    const x = Math.max(0, (box.x - padX) * width);
    const y = Math.max(0, (box.y - padY) * height);
    const w = Math.min(width - x, (box.w + padX * 2) * width);
    const h = Math.min(height - y, (box.h + padY * 2) * height);
    // För liten eller nästan hela bilden – då är beskärningen inte värd något.
    if (w < 64 || h < 64 || (w * h) / (width * height) > 0.92) {
      drawable.close();
      return { imageBase64: base64, mimeType };
    }

    const canvas = document.createElement("canvas");
    canvas.width = Math.round(w);
    canvas.height = Math.round(h);
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      drawable.close();
      return { imageBase64: base64, mimeType };
    }
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(drawable.source, x, y, w, h, 0, 0, canvas.width, canvas.height);
    drawable.close();

    const out = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality),
    );
    if (!out) return { imageBase64: base64, mimeType };
    const dataUrl = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(out);
    });
    return { imageBase64: dataUrl.split(",")[1] ?? base64, mimeType: "image/jpeg" };
  } catch {
    return { imageBase64: base64, mimeType };
  }
}
